
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useUser } from '@/contexts/UserContext';
import { Cliente } from '@shared/types';
import { useSortableTable } from '@/hooks/useSortableTable';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { ExportDialog } from '@/components/ExportDialog';
import { ChevronsUpDown, Download, Loader2, Pencil, Plus, Trash } from 'lucide-react';
import * as XLSX from 'xlsx';

export default function Clientes() {
  const { fetchWithAuth } = useUser();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isExportDialog, setExportDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const navigate = useNavigate();

  const loadClientes = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth('/clientes');
      if (!response.ok) throw new Error('No se pudieron cargar los clientes');
      const data = await response.json();
      setClientes(data);
    } catch (error) {
      console.error('Error loading clientes:', error);
      toast.error('Error al cargar', { description: error instanceof Error ? error.message : 'Ocurrió un error inesperado.' });
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadClientes();
  }, [loadClientes]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return clientes;
    return clientes.filter(
      (c) =>
        c.nombre.toLowerCase().includes(q) ||
        (c.nif ?? '').toLowerCase().includes(q) ||
        (c.email ?? '').toLowerCase().includes(q)
    );
  }, [clientes, search]);

  const { sorted, sortBy, sortKey } = useSortableTable(filtered, 'nombre');

  const handleRemoveConfirm = () => {
    if (!deleteId) return;

    const promise = fetchWithAuth(`/clientes/${deleteId}`, { method: 'DELETE' })
      .then(res => {
        if (!res.ok) throw new Error('No se pudo eliminar el cliente');
        return res;
      });

    toast.promise(promise, {
      loading: 'Eliminando cliente...',
      success: () => {
        loadClientes();
        setDeleteId(null);
        return 'Cliente eliminado correctamente.';
      },
      error: (err) => `Error: ${err.message}`
    });
  };

  const allHeaders = ['Nombre', 'NIF/CIF', 'Email', 'Teléfono', 'Persona de Contacto', 'Dirección'];

  const handleExport = (selectedHeaders: string[]) => {
    const dataToExport = sorted.map(c => {
        const row: { [key: string]: any } = {};
        selectedHeaders.forEach(header => {
            switch (header) {
                case 'Nombre': row[header] = c.nombre; break;
                case 'NIF/CIF': row[header] = c.nif; break;
                case 'Email': row[header] = c.email; break;
                case 'Teléfono': row[header] = c.telefono; break;
                case 'Persona de Contacto': row[header] = c.personaContacto; break;
                case 'Dirección': row[header] = c.direccion; break;
            }
        });
        return row;
    });

    const ws = XLSX.utils.json_to_sheet(dataToExport, { header: selectedHeaders });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Clientes");
    XLSX.writeFile(wb, "clientes.xlsx");
    toast.success("Exportación completada", { description: "El fichero de clientes se ha descargado." });
  }

  const SortableHeader: React.FC<{ column: keyof Cliente, label: string }> = ({ column, label }) => (
    <TableHead onClick={() => sortBy(column)} className="cursor-pointer hover:bg-muted/50 transition-colors">
      <div className="flex items-center gap-2">
        {label}
        {sortKey === column && <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />}
      </div>
    </TableHead>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
        <div>
            <h1 className="text-3xl font-bold tracking-tighter">Clientes</h1>
            <p className="text-muted-foreground mt-1">Gestiona tus clientes de forma centralizada.</p>
        </div>
        <div className="flex gap-2">
            <Button variant="outline" onClick={() => setExportDialog(true)} disabled={clientes.length === 0}>
                <Download className="mr-2 h-4 w-4" /> Exportar
            </Button>
            <Button onClick={() => navigate('/clientes/new')}>
                <Plus className="mr-2 h-4 w-4" /> Nuevo Cliente
            </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Listado de Clientes</CardTitle>
          <CardDescription>Aquí puedes ver, buscar y editar todos tus clientes.</CardDescription>
          <div className="mt-4">
            <Input
                placeholder="Buscar por nombre, NIF o email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-sm"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader column="nombre" label="Nombre" />
                  <SortableHeader column="nif" label="NIF/CIF" />
                  <TableHead>Email</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead className="text-right pr-6">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                 {isLoading ? (
                    <TableRow>
                        <TableCell colSpan={5} className="h-24 text-center">
                            <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
                        </TableCell>
                    </TableRow>
                 ) : sorted.length > 0 ? sorted.map((c) => (
                  <TableRow key={c.id} onDoubleClick={() => navigate(`/clientes/${c.id}/edit`)} className="hover:bg-muted/50 cursor-pointer">
                    <TableCell className="font-medium">{c.nombre}</TableCell>
                    <TableCell className="text-muted-foreground">{c.nif}</TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">{c.email}</TableCell>
                    <TableCell className="hidden md:table-cell text-muted-foreground">{c.telefono}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end items-center">
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/clientes/${c.id}/edit`); }}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setDeleteId(c.id ?? null); }}>
                          <Trash className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">No has añadido ningún cliente todavía.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      
      <ExportDialog 
        open={isExportDialog} 
        onOpenChange={setExportDialog} 
        allHeaders={allHeaders}
        onExport={handleExport}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(isOpen) => !isOpen && setDeleteId(null)}>
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                <AlertDialogDescription>Esta acción es irreversible y eliminará al cliente permanentemente. Podría afectar a documentos asociados.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel onClick={() => setDeleteId(null)}>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleRemoveConfirm}>Confirmar y Eliminar</AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
    </div>
  );
}
